import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import { rgba } from 'polished';
import React, { useCallback, useRef, useState } from 'react';
import { PopperChildrenProps, usePopper } from 'react-popper';
import { compose, padding, PaddingProps } from 'styled-system';

import { Portal } from '../Portal';

type TooltipChildProps = React.HTMLAttributes<HTMLElement> & {
  ref?: React.Ref<HTMLElement>;
};

type Props = {
  placement?: PopperChildrenProps['placement'];
  content: React.ReactNode;
  popperStyle?: PaddingProps;
  children: React.ReactElement<TooltipChildProps>;
};

const setRef = (
  ref: React.Ref<HTMLElement> | null | undefined,
  value: HTMLElement | null,
): void => {
  if (ref == null) return;
  if (typeof ref === 'function') {
    ref(value);
  } else {
    (ref as React.MutableRefObject<HTMLElement | null>).current = value;
  }
};

const Popper = styled('div', { shouldForwardProp })(
  ({ theme }) =>
    css({
      background: rgba(theme.colors.background.black, 0.9),
      fontSize: theme.fontSizes.s,
      fontWeight: theme.fontWeights.bold,
      borderRadius: theme.radii.default,
      padding: theme.space.m,
      color: theme.colors.text.white,
      zIndex: theme.zIndices.tooltip,
    }),
  () => compose(padding),
);

export const Tooltip = React.memo((props: Props) => {
  const { onMouseOver, onMouseLeave, ref: childRef, ...childProps } = props.children.props;
  const [isOpen, setIsOpen] = useState(false);
  const referenceRef = useRef<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(referenceRef.current, popperElement, {
    placement: props.placement || 'top',
  });
  const handleRef = useCallback(
    (refValue: HTMLElement | null) => {
      setRef(childRef, refValue);
      setRef(referenceRef, refValue);
    },
    [childRef],
  );
  const handleEnter = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (onMouseOver) {
        onMouseOver(e);
      }
      setIsOpen(true);
    },
    [onMouseOver],
  );
  const handleLeave = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (onMouseLeave) {
        onMouseLeave(e);
      }
      setIsOpen(false);
    },
    [onMouseLeave],
  );

  return (
    <>
      {React.cloneElement(props.children, {
        ...childProps,
        ref: handleRef,
        onMouseOver: handleEnter,
        onMouseLeave: handleLeave,
      })}
      {isOpen && (
        <Portal>
          <Popper
            ref={(el: HTMLElement | null) => setPopperElement(el)}
            style={styles.popper}
            {...props.popperStyle}
            {...attributes.popper}
          >
            {props.content}
          </Popper>
        </Portal>
      )}
    </>
  );
});

Tooltip.displayName = 'Tooltip';
